// frontend/src/pages/Settings/Settings.js
import React, { useState, lazy, Suspense } from 'react';
import './Settings.css';

// Lazy load the user management component
const SettingsUserManagement = lazy(() => import('./SettingsUserManagement'));

function Settings() {
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    companyName: 'ERP System Solutions',
    currency: 'LKR',
    taxRate: 8,
    lowStockThreshold: 10,
    invoicePrefix: 'INV',
    quotationPrefix: 'QUOT',
    rentalPrefix: 'RENT',
    notifications: {
      email: true,
      push: true,
      lowStock: true,
      overdue: true
    }
  });

  // Permission system tabs
  const [permissionTab, setPermissionTab] = useState('admin');
  const [showPermissions, setShowPermissions] = useState(false);

  // Main settings tabs
  const mainTabs = [
    { id: 'general', label: 'General Settings', icon: '⚙️' },
    { id: 'company', label: 'Company Info', icon: '🏢' },
    { id: 'tus', label: 'Tus Settings', icon: '🔧' },
    { id: 'notifications', label: 'Notifications', icon: '🔔' },
    { id: 'users', label: 'User Management', icon: '👥' },
  ];

  const permissionTabs = [
    { id: 'admin', label: 'System Administrator' },
    { id: 'error', label: 'Error System' },
    { id: 'settings', label: 'Settings' },
  ];

  const permissionData = {
    admin: [
      { id: 'dashboard', label: 'Dashboard', enabled: true },
      { id: 'pos', label: 'POS', enabled: true },
      { id: 'pos_sales', label: 'POS Sales', enabled: true },
      { id: 'products', label: 'Products', enabled: true },
      { id: 'customers', label: 'Customers', enabled: true },
      { id: 'credit_dashboard', label: 'Credit Dashboard', enabled: true },
      { id: 'payment_collection', label: 'Payment Collection', enabled: true },
      { id: 'credit_customers', label: 'Credit Customers', enabled: true },
      { id: 'overdue_accounts', label: 'Overdue Accounts', enabled: true },
      { id: 'repairs', label: 'Repairs', enabled: true },
      { id: 'invoices', label: 'Invoices', enabled: true },
      { id: 'sales', label: 'Sales', enabled: true },
      { id: 'quotations', label: 'Quotations', enabled: true },
    ],
    error: [
      { id: 'error_dashboard', label: 'Dashboard', enabled: false },
      { id: 'error_pos', label: 'POS', enabled: false },
      { id: 'error_pos_sales', label: 'POS Sales', enabled: false },
      { id: 'error_products', label: 'Products', enabled: false },
      { id: 'error_customers', label: 'Customers', enabled: false },
      { id: 'error_credit_dashboard', label: 'Credit Dashboard', enabled: false },
      { id: 'error_payment_collection', label: 'Payment Collection', enabled: false },
      { id: 'error_credit_customers', label: 'Credit Customers', enabled: false },
      { id: 'error_overdue_accounts', label: 'Overdue Accounts', enabled: false },
      { id: 'error_repairs', label: 'Repairs', enabled: false },
      { id: 'error_invoices', label: 'Invoices', enabled: false },
      { id: 'error_sales', label: 'Sales', enabled: false },
      { id: 'error_quotations', label: 'Quotations', enabled: false },
    ],
    settings: [
      { id: 'settings_dashboard', label: 'Dashboard', enabled: true },
      { id: 'settings_pos', label: 'POS', enabled: true },
      { id: 'settings_pos_sales', label: 'POS Sales', enabled: true },
      { id: 'settings_products', label: 'Products', enabled: true },
      { id: 'settings_customers', label: 'Customers', enabled: true },
      { id: 'settings_credit_dashboard', label: 'Credit Dashboard', enabled: true },
      { id: 'settings_payment_collection', label: 'Payment Collection', enabled: true },
      { id: 'settings_credit_customers', label: 'Credit Customers', enabled: true },
      { id: 'settings_overdue_accounts', label: 'Overdue Accounts', enabled: true },
      { id: 'settings_repairs', label: 'Repairs', enabled: true },
      { id: 'settings_invoices', label: 'Invoices', enabled: true },
    ]
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleNotificationChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [key]: value
      }
    }));
  };

  const handlePermissionChange = (system, id, enabled) => {
    console.log(`Permission changed: ${system}.${id} = ${enabled}`);
  };

  const saveSettings = () => {
    alert('Settings saved successfully!');
  };

  const resetToDefault = () => {
    if (window.confirm('Are you sure you want to reset all settings to default?')) {
      setSettings({
        companyName: 'ERP System Solutions',
        currency: 'LKR',
        taxRate: 8,
        lowStockThreshold: 10,
        invoicePrefix: 'INV',
        quotationPrefix: 'QUOT',
        rentalPrefix: 'RENT',
        notifications: {
          email: true,
          push: true,
          lowStock: true,
          overdue: true
        }
      });
      alert('Settings reset to default!');
    }
  };

  const openPermissions = () => {
    setShowPermissions(true);
  };

  const closePermissions = () => {
    setShowPermissions(false);
  };

  return (
    <div className="settings-container">
      <h1>Settings</h1>

      <div className="settings-layout">
        {/* Main Settings Sidebar */}
        <div className="settings-sidebar">
          {mainTabs.map(tab => (
            <button
              key={tab.id}
              className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
          
          {/* Permissions Section Link */}
          <div className="permissions-section-link">
            <div className="section-divider"></div>
            <button 
              className="permissions-btn"
              onClick={openPermissions}
            >
              <span className="tab-icon">🔒</span>
              <span className="tab-label">Permissions</span>
            </button>
          </div>
        </div>

        {/* Main Settings Content */}
        <div className="settings-content">
          {/* General Settings */}
          {activeTab === 'general' && (
            <div className="settings-form">
              <h2>General Settings</h2>
              <div className="form-group">
                <label>Default Currency</label>
                <select 
                  value={settings.currency}
                  onChange={(e) => handleSettingChange('currency', e.target.value)}
                >
                  <option value="LKR">Sri Lankan Rupee (LKR)</option>
                  <option value="USD">US Dollar (USD)</option>
                  <option value="EUR">Euro (EUR)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Default Tax Rate (%)</label>
                <input
                  type="number"
                  value={settings.taxRate}
                  onChange={(e) => handleSettingChange('taxRate', e.target.value)}
                  min="0"
                  max="100"
                  step="0.1"
                />
              </div>
              <div className="form-group">
                <label>Low Stock Threshold</label>
                <input
                  type="number"
                  value={settings.lowStockThreshold}
                  onChange={(e) => handleSettingChange('lowStockThreshold', e.target.value)}
                  min="1"
                />
              </div>
            </div>
          )}

          {/* Company Info */}
          {activeTab === 'company' && (
            <div className="settings-form">
              <h2>Company Information</h2>
              <div className="form-group">
                <label>Company Name</label>
                <input
                  type="text"
                  value={settings.companyName}
                  onChange={(e) => handleSettingChange('companyName', e.target.value)}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Invoice Prefix</label>
                  <input
                    type="text"
                    value={settings.invoicePrefix}
                    onChange={(e) => handleSettingChange('invoicePrefix', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Quotation Prefix</label>
                  <input
                    type="text"
                    value={settings.quotationPrefix}
                    onChange={(e) => handleSettingChange('quotationPrefix', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Rental Prefix</label>
                  <input
                    type="text"
                    value={settings.rentalPrefix}
                    onChange={(e) => handleSettingChange('rentalPrefix', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Tus Settings */}
          {activeTab === 'tus' && (
            <div className="settings-form">
              <h2>Tus Settings</h2>
              <div className="form-group">
                <label>Tus Server URL</label>
                <input
                  type="url"
                  placeholder="https://tus.yourserver.com"
                />
              </div>
              <div className="form-group">
                <label>Maximum File Size (MB)</label>
                <input
                  type="number"
                  defaultValue="100"
                  min="1"
                  max="1000"
                />
              </div>
            </div>
          )}

          {/* Notifications */}
          {activeTab === 'notifications' && (
            <div className="settings-form">
              <h2>Notification Settings</h2>
              <div className="notification-settings">
                <div className="notification-item">
                  <div className="notification-info">
                    <h4>Email Notifications</h4>
                    <p>Receive notifications via email</p>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={settings.notifications.email}
                      onChange={(e) => handleNotificationChange('email', e.target.checked)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
                <div className="notification-item">
                  <div className="notification-info">
                    <h4>Push Notifications</h4>
                    <p>Receive push notifications in browser</p>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={settings.notifications.push}
                      onChange={(e) => handleNotificationChange('push', e.target.checked)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
                <div className="notification-item">
                  <div className="notification-info">
                    <h4>Low Stock Alerts</h4>
                    <p>Get notified when products are low in stock</p>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={settings.notifications.lowStock}
                      onChange={(e) => handleNotificationChange('lowStock', e.target.checked)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* User Management - Integrated Dashboard */}
          {activeTab === 'users' && (
            <div className="settings-user-management-container">
              <Suspense fallback={
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading User Management...</p>
                </div>
              }>
                <SettingsUserManagement />
              </Suspense>
            </div>
          )}

          {/* Save/Reset Buttons - Hide when in User Management tab */}
          {activeTab !== 'users' && (
            <div className="settings-actions">
              <button className="btn-primary" onClick={saveSettings}>
                Save Settings
              </button>
              <button className="btn-secondary" onClick={resetToDefault}>
                Reset to Default
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Permissions Modal/Window */}
      {showPermissions && (
        <div className="permissions-modal-overlay">
          <div className="permissions-modal">
            <div className="permissions-header">
              <h2>Permissions Management</h2>
              <button className="close-btn" onClick={closePermissions}>×</button>
            </div>
            
            <div className="permissions-tabs">
              {permissionTabs.map(tab => (
                <button
                  key={tab.id}
                  className={`permission-tab ${permissionTab === tab.id ? 'active' : ''}`}
                  onClick={() => setPermissionTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="permissions-content">
              <div className="permissions-section">
                <h3>{permissionTabs.find(t => t.id === permissionTab)?.label}</h3>
                <div className="permissions-grid">
                  {permissionData[permissionTab]?.map(permission => (
                    <div key={permission.id} className="permission-item">
                      <label className="permission-label">
                        <input
                          type="checkbox"
                          checked={permission.enabled}
                          onChange={(e) => handlePermissionChange(permissionTab, permission.id, e.target.checked)}
                        />
                        <span className="permission-text">{permission.label}</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="permissions-actions">
              <button className="btn-primary" onClick={closePermissions}>
                Save Permissions
              </button>
              <button className="btn-secondary" onClick={closePermissions}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;